import { useEffect, useState } from "react";
import { toast } from "sonner";
import { pluginsApi } from "@/lib/api";
import type { AvailablePlugin, PluginAuthorization } from "@/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { BrandLogo, SETTINGS_BUTTONS, SettingsBlock, SettingsPane, SettingsRow, SettingsSection, StatusPill } from "./SettingsLayout";
import { timeAgo } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";

// The OAuth authorize URL for the ChatGPT plugin
const CHATGPT_AUTH_URL = "https://auth.peersheep.com/oauth/authorize";
const CHATGPT_CLIENT_ID = "chatgpt-plugin";

// Built-in plugin definitions — the backend may also serve these via GET /plugins
const BUILTIN_PLUGINS: AvailablePlugin[] = [
  {
    id: "chatgpt-plugin",
    name: "ChatGPT",
    description:
      "Ask ChatGPT questions about your competitors using your Peersheep data. Once authorized, the plugin can read alerts, watched URLs and snapshots via the MCP endpoint.",
    logoUrl: null,
    authorizationUrl: CHATGPT_AUTH_URL,
  },
];

function pluginAuthUrl(plugin: AvailablePlugin): string {
  // Redirect back to the settings page; the callback handler stores the token
  const returnUrl = `${window.location.origin}/settings?tab=plugins`;
  const params = new URLSearchParams({
    client_id: CHATGPT_CLIENT_ID,
    response_type: "code",
    redirect_uri: returnUrl,
    scope: "competitors:read alerts:read",
    state: `plugin:${plugin.id}`,
  });
  return `${plugin.authorizationUrl}?${params.toString()}`;
}

// ─── PluginsTab ───────────────────────────────────────────────────────────────

export function PluginsTab() {
  const [searchParams] = useSearchParams();
  const [plugins, setPlugins] = useState<AvailablePlugin[]>(BUILTIN_PLUGINS);
  const [authorizations, setAuthorizations] = useState<PluginAuthorization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<PluginAuthorization | null>(null);

  const load = async () => {
    setLoadError(null);
    setIsLoading(true);
    const [pluginsRes, authsRes] = await Promise.all([
      pluginsApi.list(),
      pluginsApi.authorizations(),
    ]);
    if ("data" in pluginsRes && pluginsRes.data && pluginsRes.data.length > 0) {
      setPlugins(pluginsRes.data);
    }
    if ("data" in authsRes && authsRes.data) {
      setAuthorizations(authsRes.data);
    } else if (authsRes.error) {
      setLoadError(authsRes.error.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  // Handle OAuth callback: ?code=xxx&state=plugin:chatgpt-plugin
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (!code || !state?.startsWith("plugin:")) return;

    const pluginId = state.replace("plugin:", "");

    // Exchange the code for a token via the OAuth token endpoint, then store it
    const exchangeAndStore = async () => {
      try {
        const tokenRes = await fetch("https://auth.peersheep.com/oauth/token", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            code,
            redirect_uri: `${window.location.origin}/settings?tab=plugins`,
            client_id: "chatgpt-plugin",
          }),
        });

        if (!tokenRes.ok) {
          toast.error("Failed to complete plugin authorization");
          return;
        }

        const tokenData = await tokenRes.json() as {
          access_token: string;
          token_type: string;
          expires_in?: number;
        };

        const res = await pluginsApi.authorize({
          pluginId,
          accessToken: tokenData.access_token,
          expiresIn: tokenData.expires_in,
        });

        if ("data" in res && res.data) {
          toast.success("Plugin authorized successfully");
          // Clean the OAuth params from the URL without a reload
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.toString());
          void load();
        } else if (res.error) {
          toast.error(res.error.message);
        }
      } catch {
        toast.error("Plugin authorization failed");
      }
    };

    void exchangeAndStore();
  }, []);

  const handleConnect = (plugin: AvailablePlugin) => {
    window.location.href = pluginAuthUrl(plugin);
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setRevokingId(confirmRevoke.id);
    const res = await pluginsApi.revoke(confirmRevoke.id);
    if ("data" in res && res.data?.success) {
      setAuthorizations((prev) => prev.filter((a) => a.id !== confirmRevoke.id));
      toast.success(`${confirmRevoke.pluginName} access revoked`);
    } else if (res.error) {
      toast.error(res.error.message);
    }
    setRevokingId(null);
    setConfirmRevoke(null);
  };

  if (!isLoading && loadError) {
    return (
      <SettingsPane title="Plugins" description="Authorize third-party AI assistants to access your competitive data">
        <SettingsSection
          icon="alert-circle"
          title="Couldn't load plugin authorizations"
          description={loadError}
          actions={<Button size="sm" variant="outline" onClick={() => void load()}>Retry</Button>}
        />
      </SettingsPane>
    );
  }

  if (isLoading) return <PluginsSkeleton />;

  const activeAuths = authorizations.filter((a) => a.status === "active");
  const authCount = activeAuths.length;

  return (
    <SettingsPane
      title="Plugins"
      description={
        authCount > 0
          ? `${authCount} authorized · AI assistants can read your competitive data`
          : "Authorize AI assistants to read your competitive data via MCP"
      }
    >
      {plugins.map((plugin) => {
        const auth = activeAuths.find((a) => a.pluginId === plugin.id);
        const isAuthorized = !!auth;

        return (
          <SettingsSection
            key={plugin.id}
            icon={<BrandLogo domain="openai.com" name={plugin.name} fallbackIcon="cpu-01" />}
            title={plugin.name}
            description={plugin.description}
            badge={isAuthorized && <StatusPill tone="ok">Authorized</StatusPill>}
            actions={
              isAuthorized ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setConfirmRevoke(auth)}
                  disabled={revokingId === auth.id}
                >
                  {revokingId === auth.id
                    ? <Icon name="refresh" size={12} className="animate-spin" />
                    : "Revoke"}
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleConnect(plugin)}>
                  Authorize
                </Button>
              )
            }
          >
            {isAuthorized ? (
              <>
                <SettingsRow
                  label="Access"
                  hint="This plugin can read your alerts, competitors, and watched page snapshots."
                >
                  <span className="font-mono text-[11px] text-muted-foreground">Read-only</span>
                </SettingsRow>
                <SettingsRow
                  label="Authorized"
                  hint="When this plugin was last granted access."
                >
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {timeAgo(auth.authorizedAt)}
                  </span>
                </SettingsRow>
                {auth.lastUsedAt && (
                  <SettingsRow
                    label="Last used"
                    hint="When the plugin last called the MCP endpoint."
                  >
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {timeAgo(auth.lastUsedAt)}
                    </span>
                  </SettingsRow>
                )}
                {auth.expiresAt && (
                  <SettingsRow label="Expires" hint="After this date the plugin must re-authorize.">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {new Date(auth.expiresAt).toLocaleDateString()}
                    </span>
                  </SettingsRow>
                )}
              </>
            ) : (
              <SettingsBlock>
                <p className="font-sans text-xs text-muted-foreground">
                  Click <span className="font-medium text-foreground">Authorize</span> to start an
                  OAuth flow. After you approve access, the plugin receives a token scoped to your
                  workspace and can read your Peersheep data through the MCP endpoint.
                </p>
                <McpEndpointHint />
              </SettingsBlock>
            )}
          </SettingsSection>
        );
      })}

      {/* MCP endpoint info for power users */}
      <SettingsSection
        icon="api"
        title="MCP endpoint"
        description="Connect any MCP-compatible client using a personal access token"
      >
        <SettingsBlock className="space-y-3">
          <p className="font-sans text-xs text-muted-foreground">
            The MCP server is available at{" "}
            <code className="font-mono text-[11px] text-foreground">
              https://api.peersheep.com/mcp
            </code>
            . Pass your API key or an OAuth2 bearer token in the{" "}
            <code className="font-mono text-[11px] text-foreground">Authorization</code> header.
          </p>
          <p className="font-sans text-[11px] text-muted-foreground">
            For custom clients, generate an API key under{" "}
            <span className="font-medium text-foreground">Settings → API</span> and use it as a
            bearer token.
          </p>
        </SettingsBlock>
      </SettingsSection>

      {/* Confirm revoke dialog */}
      <Dialog open={!!confirmRevoke} onOpenChange={(open) => !open && setConfirmRevoke(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Revoke {confirmRevoke?.pluginName} access?</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-xs text-muted-foreground">
            The plugin's token will stop working immediately. It will need to re-authorize before it
            can access your data again.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleRevoke()}>
              Revoke access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPane>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function McpEndpointHint() {
  return (
    <p className="mt-2 font-sans text-[11px] text-muted-foreground">
      MCP endpoint:{" "}
      <code className="font-mono text-foreground">https://api.peersheep.com/mcp</code>
    </p>
  );
}

function PluginsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <div className="space-y-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-3 w-72" />
      </div>
      {[1, 1].map((rows, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-md" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-20" />
                <Skeleton className="h-3 w-64" />
              </div>
            </div>
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} className="flex items-center justify-between border-t border-border px-5 py-4 first:border-t-0">
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-52" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
